import { Button, Typography, getTypographyDefaultColor, useMediaQuery } from '@ag1/ui';
import { vars } from '@ag1/ui/theme';
import { toPlainText } from '@portabletext/react';
import { buttonClickEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { usePageContext } from 'processes/Page/PageContext';
import { useMemo } from 'react';
import { HsaFsaAvailability } from 'shared/components/HSA-FSA/HsaFsaAvailability';
import { useLocale } from 'shared/hooks/useLocale';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import UnifiedButton from 'shared/studio/UnifiedButton/UnifiedButton';
import { type UnifiedButtonType } from 'shared/studio/UnifiedButton/UnifiedButton.types';
import { HeroHalfV5 } from 'types/studio/studio';
import { getSanityLinkUrl } from '../../../utils/getSanityLinkUrl';
import { StudioBaseIconV2 } from '../../StudioBaseIcon/StudioBaseIcon';
import HeroHalfV5Benefits from '../HeroHalfV5Benefits/HeroHalfV5Benefits';
import HeroHalfV5HeadlineAndSubtitle from '../HeroHalfV5HeadlineAndSubtitle/HeroHalfV5HeadlineAndSubtitle';
import styles from './HeroHalfV5Content.module.scss';

const { colors } = vars;

const RTBTags = ({
  rtbTagsTextColor,
  rtbTagsTextColorMobile,
  rtbTags
}: {
  rtbTagsTextColor: HeroHalfV5['rtbTagsTextColor'];
  rtbTagsTextColorMobile: HeroHalfV5['rtbTagsTextColorMobile'];
  rtbTags: HeroHalfV5['rtbTags'];
}) => {
  const aboveMedium = useMediaQuery('m');

  if (!rtbTags) return null;

  const textColor = aboveMedium ? rtbTagsTextColor : rtbTagsTextColorMobile;

  return (
    <div className={styles.rtbTags}>
      {rtbTags.map((tag) => {
        const icon = aboveMedium ? tag.icon : tag.iconMobile;
        return (
          <div key={tag._key} className={styles.tagContainer}>
            {icon && <StudioBaseIconV2 icon={icon} className={styles.tagIcon} />}
            {tag.text && (
              <Typography
                variant="body"
                size="1"
                noPadding
                className={styles.tagTextContainer}
                style={{
                  color: textColor === 'light' ? colors.text.primaryInverse : colors.text.primary
                }}>
                <CustomPortableText value={tag.text} className={styles.tagText} />
              </Typography>
            )}
          </div>
        );
      })}
    </div>
  );
};

type Props = {
  sectionType: string;
  theme: 'old' | 'new' | undefined;
  visibleOnPageLoad: boolean;
  isDarkMode: boolean;
} & Pick<
  HeroHalfV5,
  | 'mediaSizeNarrow'
  | 'contentPosition'
  | 'isFullScreenBg'
  | 'textAlignment'
  | 'superHeadline'
  | 'superHeadlineVariation'
  | 'subtitle'
  | 'logo'
  | 'logoSize'
  | 'logoPosition'
  | 'unifiedButton'
  | 'hasButtonOnMobile'
  | 'unifiedButtonMobile'
  | 'additionalLinkSwitch'
  | 'additionalLink'
  | 'hasAdditionalLinkOnMobile'
  | 'additionalLinkMobile'
  | 'additionalTarget'
  | 'hoverColor'
  | 'headline'
  | 'bodyText'
  | 'caption'
  | 'captionIcon'
  | 'captionIconMobile'
  | 'ratingTitle'
  | 'ratingValue'
  | 'ratingMobilePosition'
  | 'ratingStarColor'
  | 'ratingStarColorMobile'
  | 'benefitsList'
  | 'benefitsAlignToStart'
  | 'showTruemedEligibility'
  | 'footnote'
  | 'showfootnote'
  | 'rtbTagsTextColor'
  | 'rtbTagsTextColorMobile'
  | 'rtbTags'
  | 'hugPadding'
>;

const HeroHalfV5Content = ({
  sectionType,
  mediaSizeNarrow,
  textAlignment,
  contentPosition,
  isFullScreenBg,
  superHeadline,
  superHeadlineVariation,
  subtitle,
  logo,
  logoSize,
  logoPosition,
  unifiedButton,
  hasButtonOnMobile,
  unifiedButtonMobile,
  additionalLinkSwitch,
  additionalLink,
  hasAdditionalLinkOnMobile,
  additionalLinkMobile,
  additionalTarget,
  hoverColor,
  headline,
  bodyText,
  caption,
  captionIcon,
  captionIconMobile,
  ratingTitle,
  ratingValue,
  ratingMobilePosition,
  ratingStarColor,
  ratingStarColorMobile,
  benefitsList,
  benefitsAlignToStart,
  theme,
  showTruemedEligibility,
  footnote,
  showfootnote,
  rtbTagsTextColor,
  rtbTagsTextColorMobile,
  rtbTags,
  hugPadding = 'medium',
  visibleOnPageLoad,
  isDarkMode
}: Props) => {
  const aboveMedium = useMediaQuery('m');
  const locale = useLocale();

  const { shouldDisableUnifiedButton, shouldDisableCTALink, maybeReplaceOfferlessCheckoutLink } =
    usePageContext();

  const disableUnifiedButton = useMemo(
    () => shouldDisableUnifiedButton(unifiedButton as UnifiedButtonType),
    [shouldDisableUnifiedButton, unifiedButton]
  );

  const additionalLinkUrl = useMemo(
    () => maybeReplaceOfferlessCheckoutLink(getSanityLinkUrl(additionalLink?.btnLink, locale)),
    [maybeReplaceOfferlessCheckoutLink, additionalLink?.btnLink, locale]
  );

  const shouldShowCaptionIconMobile = !aboveMedium && !!captionIconMobile;

  const handleAdditionalLinkButtonClick = () => {
    buttonClickEvent({
      clickText: additionalLink?.portableText ? toPlainText(additionalLink?.portableText) : '',
      url: additionalLinkUrl,
      location: sectionType
    });
  };

  return (
    <div
      className={clsx(
        styles.content,
        mediaSizeNarrow && styles.mediaNarrow,
        textAlignment === 'left' && styles.left,
        contentPosition === 'center' && styles.centerContent,
        isFullScreenBg && styles[`content-padding-${hugPadding}`]
      )}>
      <HeroHalfV5HeadlineAndSubtitle
        headline={headline}
        subtitle={subtitle}
        superHeadline={superHeadline}
        superHeadlineVariation={superHeadlineVariation}
        logo={logo}
        logoSize={logoSize}
        logoPosition={logoPosition}
        textAlignment={textAlignment}
        contentPosition={contentPosition}
        ratingValue={ratingValue}
        ratingTitle={ratingTitle}
        ratingMobilePosition={ratingMobilePosition}
        ratingStarColor={ratingStarColor}
        ratingStarColorMobile={ratingStarColorMobile}
        theme={theme}
        visibleOnPageLoad={visibleOnPageLoad}
        isDarkMode={isDarkMode}
      />
      {bodyText && (
        <Typography
          component="div"
          variant="body"
          size="3"
          weight="400"
          noPadding
          className={clsx(styles.text, isDarkMode && styles.isDarkMode)}
          color={getTypographyDefaultColor(theme)}>
          <CustomPortableText
            key={JSON.stringify(bodyText.text)}
            addAccessibility
            value={bodyText?.text || []}
            className={clsx(
              styles.bodyText,
              bodyText.textFontSizeDesktop !== '18px' && styles.fontBiggerDesktop,
              bodyText.textFontSizeMobile !== '16px' && styles.fontBiggerMobile,
              bodyText.lineSpacing && styles.lineSpacing,
              subtitle && styles.underSubtitle,
              bodyText.bodyTextPositionOnMobile === 'above' && styles.aboveCta,
              bodyText.textSize === 'm' && styles.medium,
              bodyText.textSize === 'l' && styles.large
            )}
          />
        </Typography>
      )}
      {bodyText && bodyText.textBold && bodyText.textBold.length > 0 && (
        <Typography
          component="div"
          variant="body"
          size="2"
          weight="400"
          noPadding
          className={clsx(styles.text, isDarkMode && styles.isDarkMode)}
          color={getTypographyDefaultColor(theme)}>
          <CustomPortableText
            addAccessibility
            value={bodyText.textBold}
            className={styles.textBold}
          />
        </Typography>
      )}
      {unifiedButton?.switchButton !== 'noButton' && (
        <div
          className={clsx(
            styles.buttonWrapper,
            textAlignment === 'left' && styles.start,
            contentPosition === 'center' && styles.centerButton
          )}>
          {hasButtonOnMobile && (
            <UnifiedButton
              unifiedButton={unifiedButtonMobile as unknown as UnifiedButtonType}
              sectionType={sectionType}
              className={clsx(
                styles.button,
                !additionalLinkSwitch && styles.onlyChild,
                styles.shouldShowOnMobile
              )}
              size="medium"
              theme={theme}
              isDisabled={disableUnifiedButton}
            />
          )}
          <UnifiedButton
            unifiedButton={unifiedButton as UnifiedButtonType}
            sectionType={sectionType}
            className={clsx(
              styles.button,
              !additionalLinkSwitch && styles.onlyChild,
              hasButtonOnMobile && styles.shouldHideOnMobile
            )}
            size="medium"
            theme={theme}
            isDisabled={disableUnifiedButton}
          />
          {additionalLinkSwitch && additionalLink && additionalLinkUrl && (
            <>
              {hasAdditionalLinkOnMobile && (
                <Button
                  className={clsx(
                    styles.button,
                    hoverColor === 'dark' && styles.dark,
                    textAlignment === 'left' && styles.btnLeft,
                    styles.shouldShowOnMobile
                  )}
                  size={{ l: 'large', xxs: 'medium' }}
                  href={additionalLinkUrl}
                  disabled={shouldDisableCTALink(additionalLinkUrl)}
                  variant={additionalLinkMobile?.variant}
                  rounded
                  icon={additionalLinkMobile?.showIcon && additionalLinkMobile?.iconOptions?.icon}
                  iconPosition={additionalLinkMobile?.iconOptions?.position}
                  data-gtm-skip-global
                  onClick={handleAdditionalLinkButtonClick}
                  target={additionalTarget ? '_blank' : '_self'}
                  theme={theme}>
                  {additionalLinkMobile?.portableText && (
                    <CustomPortableText value={additionalLinkMobile?.portableText} />
                  )}
                </Button>
              )}
              <Button
                className={clsx(
                  styles.button,
                  hoverColor === 'dark' && styles.dark,
                  textAlignment === 'left' && styles.btnLeft,
                  hasAdditionalLinkOnMobile && styles.shouldHideOnMobile
                )}
                size={{ l: 'large', xxs: 'medium' }}
                href={additionalLinkUrl}
                disabled={shouldDisableCTALink(additionalLinkUrl)}
                variant={additionalLink.variant}
                rounded
                icon={additionalLink.showIcon && additionalLink.iconOptions?.icon}
                iconPosition={additionalLink.iconOptions?.position}
                data-gtm-skip-global
                onClick={handleAdditionalLinkButtonClick}
                target={additionalTarget ? '_blank' : '_self'}
                theme={theme}>
                {additionalLink.portableText && (
                  <CustomPortableText value={additionalLink.portableText} />
                )}
              </Button>
            </>
          )}
        </div>
      )}
      {caption && (
        <div className={clsx(styles.caption, textAlignment === 'left' && styles.start)}>
          {!shouldShowCaptionIconMobile && captionIcon?.asset && (
            <StudioBaseImageV2
              image={captionIcon}
              className={styles.icon}
              eagerLoading={visibleOnPageLoad}
            />
          )}
          {shouldShowCaptionIconMobile && captionIconMobile?.asset && (
            <StudioBaseImageV2
              image={captionIconMobile}
              className={styles.icon}
              eagerLoading={visibleOnPageLoad}
            />
          )}
          <Typography
            variant="portalCaption"
            size="1"
            noPadding
            color={getTypographyDefaultColor(theme)}
            className={clsx(styles.portalCaption, isDarkMode && styles.isDarkMode)}>
            {caption}
          </Typography>
        </div>
      )}
      {showTruemedEligibility && <HsaFsaAvailability sourcePage="primaryLander" />}
      {benefitsList && (
        <HeroHalfV5Benefits
          benefitsList={benefitsList}
          benefitsAlignToStart={benefitsAlignToStart}
          visibleOnPageLoad={visibleOnPageLoad}
          isDarkMode={isDarkMode}
        />
      )}
      {showfootnote && footnote && (
        <Typography
          variant="portalCaption"
          size="2"
          color="grey-1-new"
          noPadding
          className={clsx(
            styles.footnote,
            textAlignment === 'left' && styles.left,
            isDarkMode && styles.isDarkMode
          )}>
          <CustomPortableText value={footnote} />
        </Typography>
      )}
      {rtbTags && (
        <RTBTags
          rtbTags={rtbTags}
          rtbTagsTextColor={rtbTagsTextColor}
          rtbTagsTextColorMobile={rtbTagsTextColorMobile}
        />
      )}
    </div>
  );
};
export default HeroHalfV5Content;
